/**
 * カーセンサー向けファイル名生成 (純粋関数)。
 *
 * 規則 (SPEC §1.2):
 * - 1 枚目 (ヒーロー): `01_hero.png`
 * - 2〜80 枚目:       `NN.jpg` (ゼロ埋め 2 桁)
 */

import { CARSENSOR_SPEC } from './types';

/**
 * 0-based index と hero フラグからファイル名を生成。
 *
 * @param index - 0 から始まる連番
 * @param isHero - ヒーロー写真なら true (PNG 拡張子になる)
 * @throws RangeError - index が負 or 80 以上
 */
export function sequentialFileName(index: number, isHero: boolean): string {
  if (!Number.isInteger(index)) {
    throw new RangeError(`index must be an integer, got ${index}`);
  }
  if (index < 0 || index >= CARSENSOR_SPEC.MAX_PHOTOS_PER_VEHICLE) {
    throw new RangeError(
      `index must be 0..${CARSENSOR_SPEC.MAX_PHOTOS_PER_VEHICLE - 1}, got ${index}`,
    );
  }
  const num = (index + 1).toString().padStart(2, '0');
  if (isHero) {
    return `${num}_hero.png`;
  }
  return `${num}.jpg`;
}

/**
 * 車種・年式・VIN末尾 3桁から ZIP 名を組み立てる。
 *
 * 例: assembleZipName('BMW X5', 2020, 'ABC') → 'BMW_X5_2020_ABC.zip'
 *
 * 日本語・空白などはアンダースコアに置換。
 */
export function assembleZipName(model: string, year: number, vinTail3: string): string {
  if (!Number.isInteger(year) || year < 1900 || year > 2100) {
    throw new RangeError(`year must be 1900..2100, got ${year}`);
  }
  if (vinTail3.length !== 3) {
    throw new RangeError(`vinTail3 must be exactly 3 chars, got ${vinTail3.length}`);
  }
  const safe = (s: string): string =>
    s
      .trim()
      .replace(/[\s/\\]+/g, '_')
      .replace(/[^\w\-_.]/g, '_');
  const modelPart = safe(model) || 'vehicle';
  const vinPart = safe(vinTail3) || 'XXX';
  return `${modelPart}_${year}_${vinPart}.zip`;
}
